import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, IconButton, Switch,
  Divider, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip,
  CircularProgress, MenuItem, Tooltip, Paper, LinearProgress, FormControlLabel,
  Snackbar, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Email, Storage, Notifications, Settings, Send, Delete, Add, Edit,
  Refresh, Info, Warning, Error as ErrorIcon, CheckCircle, PlayArrow,
  DeleteForever, Schedule, Save, GetApp, SettingsBackupRestore, CloudUpload,
} from '@mui/icons-material';
import apiClient from '../../utils/apiClient';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

const severityColor: Record<string, string> = {
  info: '#2196f3', warning: '#ff9800', critical: '#f44336', emergency: '#b71c1c',
};

const SystemSettings: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Email state
  const [smtpConfig, setSmtpConfig] = useState({ smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_from: '', smtp_use_tls: true });
  const [smtpStatus, setSmtpStatus] = useState<any>(null);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Database state
  const [dbStats, setDbStats] = useState<any>(null);
  const [ttlStatus, setTtlStatus] = useState<any>(null);
  const [ttlDays, setTtlDays] = useState(30);
  const [purgeDays, setPurgeDays] = useState(30);
  const [dbLoading, setDbLoading] = useState(false);

  // Backup state
  const [backups, setBackups] = useState<any[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupCreating, setBackupCreating] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [autoBackupConfig, setAutoBackupConfig] = useState({ enabled: false, hour: 2, minute: 0, retention: 7 });
  const [autoBackupSaving, setAutoBackupSaving] = useState(false);

  // Alerts state
  const [alertRules, setAlertRules] = useState<any[]>([]);
  const [alertHistory, setAlertHistory] = useState<any[]>([]);
  const [alertHistoryTotal, setAlertHistoryTotal] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [ruleForm, setRuleForm] = useState({
    name: '', trigger_type: 'event', event_type: '', robot_serial: '',
    severity: 'warning', cooldown_minutes: 15, suggested_action: '',
    condition: {} as any,
  });

  // Telemetry rate state
  const [telemetryRate, setTelemetryRate] = useState(5);
  const [telemetryRateRobots, setTelemetryRateRobots] = useState<any[]>([]);
  const [telemetryRateLoading, setTelemetryRateLoading] = useState(false);
  const [telemetryRateSaving, setTelemetryRateSaving] = useState(false);
  const [selectedTelemetryRobot, setSelectedTelemetryRobot] = useState('__all__');

  // System state
  const [systemInfo, setSystemInfo] = useState<any>(null);

  const showMsg = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ===== Email Tab =====
  const loadSmtpStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/system/smtp');
      setSmtpStatus(res.data);
      setSmtpConfig(prev => ({
        ...prev,
        smtp_host: res.data.smtp_host || '',
        smtp_port: res.data.smtp_port || 587,
        smtp_user: res.data.smtp_user || '',
        smtp_from: res.data.smtp_from || '',
        smtp_use_tls: res.data.smtp_use_tls ?? true,
        smtp_password: '', // Never pre-fill password
      }));
    } catch { /* ignore */ }
  }, []);

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      await apiClient.post('/admin/system/smtp', smtpConfig);
      showMsg('SMTP configuration saved');
      await loadSmtpStatus();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to save SMTP config', 'error');
    }
    setSmtpSaving(false);
  };

  const testEmail = async () => {
    setSmtpTesting(true);
    try {
      await apiClient.post('/admin/system/smtp/test');
      showMsg('Test email sent successfully');
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to send test email', 'error');
    }
    setSmtpTesting(false);
  };

  // ===== Database Tab =====
  const loadDbStats = useCallback(async () => {
    setDbLoading(true);
    try {
      const [statsRes, ttlRes] = await Promise.all([
        apiClient.get('/admin/system/telemetry/stats'),
        apiClient.get('/admin/system/telemetry/ttl'),
      ]);
      setDbStats(statsRes.data);
      setTtlStatus(ttlRes.data);
      if (ttlRes.data?.ttl?.expire_after_days) {
        setTtlDays(Math.round(ttlRes.data.ttl.expire_after_days));
      }
    } catch { /* ignore */ }
    setDbLoading(false);
  }, []);

  const setTtl = async () => {
    try {
      await apiClient.post(`/admin/system/telemetry/ttl?days=${ttlDays}`);
      showMsg(`TTL set to ${ttlDays} days`);
      await loadDbStats();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to set TTL', 'error');
    }
  };

  const removeTtl = async () => {
    try {
      await apiClient.post('/admin/system/telemetry/ttl?days=0');
      showMsg('TTL removed — data retained indefinitely');
      await loadDbStats();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to remove TTL', 'error');
    }
  };

  const purgeData = async () => {
    if (!window.confirm(`Delete all telemetry data older than ${purgeDays} days? This cannot be undone.`)) return;
    try {
      const res = await apiClient.delete(`/admin/system/telemetry/purge?days=${purgeDays}`);
      showMsg(`Deleted ${res.data.deleted_count} records`);
      await loadDbStats();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to purge data', 'error');
    }
  };

  // ===== Backup Functions =====
  const loadBackups = useCallback(async () => {
    setBackupLoading(true);
    try {
      const res = await apiClient.get('/admin/system/backup/list');
      setBackups(res.data.backups || []);
    } catch { /* ignore */ }
    setBackupLoading(false);
  }, []);

  const createBackup = async () => {
    setBackupCreating(true);
    try {
      // v6.3.37: 30 min — the axios default of 30s aborted the request while
      // the backup kept running server-side, showing a false failure.
      const res = await apiClient.post('/admin/system/backup/create', null, { timeout: 1800000 });
      showMsg(res.data.message || 'Backup created');
      await loadBackups();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Backup failed', 'error');
    }
    setBackupCreating(false);
  };

  const downloadBackup = (filename: string) => {
    const baseUrl = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
    const token = localStorage.getItem('accessToken') || '';
    const url = `${baseUrl}/admin/system/backup/download/${encodeURIComponent(filename)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      })
      .catch(() => showMsg('Download failed', 'error'));
  };

  const restoreBackup = async (filename: string) => {
    if (!window.confirm(`Restore database from "${filename}"?\n\nThis will REPLACE all current data. Make sure you have a recent backup!`)) return;
    setRestoring(filename);
    try {
      const res = await apiClient.post(`/admin/system/backup/restore/${encodeURIComponent(filename)}`, null, { timeout: 1800000 });
      if (res.data.success) {
        showMsg(res.data.message || 'Restore complete — refreshing...');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showMsg(res.data.message || 'Restore had issues', 'error');
      }
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Restore failed', 'error');
    }
    setRestoring(null);
  };

  const deleteBackup = async (filename: string) => {
    if (!window.confirm(`Delete backup "${filename}"?`)) return;
    try {
      await apiClient.delete(`/admin/system/backup/${encodeURIComponent(filename)}`);
      showMsg('Backup deleted');
      await loadBackups();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  const loadAutoBackupConfig = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/system/backup/schedule');
      if (res.data) setAutoBackupConfig(res.data);
    } catch { /* ignore — endpoint may not exist yet */ }
  }, []);

  const saveAutoBackupConfig = async () => {
    setAutoBackupSaving(true);
    try {
      await apiClient.post('/admin/system/backup/schedule', autoBackupConfig);
      showMsg(autoBackupConfig.enabled
        ? `Auto-backup scheduled daily at ${String(autoBackupConfig.hour).padStart(2,'0')}:${String(autoBackupConfig.minute).padStart(2,'0')} (keep ${autoBackupConfig.retention} backups)`
        : 'Auto-backup disabled');
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to save schedule', 'error');
    }
    setAutoBackupSaving(false);
  };

  // ===== Alerts Tab =====
  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const [rulesRes, historyRes] = await Promise.all([
        apiClient.get('/admin/alerts/rules'),
        apiClient.get('/admin/alerts/history?limit=20'),
      ]);
      setAlertRules(rulesRes.data);
      setAlertHistory(historyRes.data.alerts || []);
      setAlertHistoryTotal(historyRes.data.total || 0);
    } catch { /* ignore */ }
    setAlertsLoading(false);
  }, []);

  const seedDefaults = async () => {
    try {
      const res = await apiClient.post('/admin/alerts/seed-defaults');
      showMsg(res.data.message);
      await loadAlerts();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to seed defaults', 'error');
    }
  };

  const openRuleDialog = (rule?: any) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        name: rule.name, trigger_type: rule.trigger_type, event_type: rule.event_type,
        robot_serial: rule.robot_serial || '', severity: rule.severity,
        cooldown_minutes: rule.cooldown_minutes, suggested_action: rule.suggested_action || '',
        condition: rule.condition || {},
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        name: '', trigger_type: 'event', event_type: '', robot_serial: '',
        severity: 'warning', cooldown_minutes: 15, suggested_action: '', condition: {},
      });
    }
    setRuleDialog(true);
  };

  const saveRule = async () => {
    const payload = { ...ruleForm, robot_serial: ruleForm.robot_serial || null };
    try {
      if (editingRule) {
        await apiClient.patch(`/admin/alerts/rules/${editingRule.id}`, payload);
        showMsg('Rule updated');
      } else {
        await apiClient.post('/admin/alerts/rules', payload);
        showMsg('Rule created');
      }
      setRuleDialog(false);
      await loadAlerts();
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to save rule', 'error');
    }
  };

  const toggleRule = async (rule: any) => {
    try {
      await apiClient.patch(`/admin/alerts/rules/${rule.id}`, { is_active: !rule.is_active });
      await loadAlerts();
    } catch { /* ignore */ }
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await apiClient.delete(`/admin/alerts/rules/${id}`);
      showMsg('Rule deleted');
      await loadAlerts();
    } catch { /* ignore */ }
  };

  // ===== System Tab =====
  const loadSystemInfo = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/system/info');
      setSystemInfo(res.data);
    } catch { /* ignore */ }
  }, []);

  // ===== Telemetry Rate =====
  const loadTelemetryRateRobots = useCallback(async () => {
    setTelemetryRateLoading(true);
    try {
      const [robotsRes, onlineRes] = await Promise.all([
        apiClient.get('/admin/robots'),
        apiClient.get('/admin/system/robots-online').catch(() => ({ data: { online: [] } })),
      ]);
      const onlineSerials: string[] = onlineRes.data?.online || [];
      const enriched = (robotsRes.data || []).map((r: any) => ({
        ...r,
        status: onlineSerials.includes(r.serial_number) ? 'online' : 'offline',
      }));
      setTelemetryRateRobots(enriched);
    } catch { /* ignore */ }
    setTelemetryRateLoading(false);
  }, []);

  const saveTelemetryRate = async () => {
    setTelemetryRateSaving(true);
    try {
      if (selectedTelemetryRobot === '__all__') {
        // Apply to all robots
        const res = await apiClient.post('/admin/system/telemetry-rate', {
          interval_seconds: telemetryRate,
          apply_to_all: true,
        });
        showMsg(res.data.message || `Telemetry rate set to ${telemetryRate}s for all robots`);
      } else {
        // Apply to single robot
        const res = await apiClient.post('/admin/system/telemetry-rate', {
          interval_seconds: telemetryRate,
          robot_serial: selectedTelemetryRobot,
        });
        showMsg(res.data.message || `Telemetry rate set to ${telemetryRate}s for ${selectedTelemetryRobot}`);
      }
    } catch (e: any) {
      showMsg(e.response?.data?.detail || 'Failed to set telemetry rate', 'error');
    }
    setTelemetryRateSaving(false);
  };

  // ===== Load on tab change =====
  useEffect(() => {
    if (tab === 0) loadSmtpStatus();
    else if (tab === 1) { loadDbStats(); loadTelemetryRateRobots(); loadBackups(); loadAutoBackupConfig(); }
    else if (tab === 2) loadAlerts();
    else if (tab === 3) loadSystemInfo();
  }, [tab, loadSmtpStatus, loadDbStats, loadAlerts, loadSystemInfo, loadTelemetryRateRobots, loadBackups, loadAutoBackupConfig]);

  // ===== Render helpers =====
  const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string }> = ({ icon, title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      {icon}
      <Box>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>System Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fleet-wide configuration — Email notifications, database management, alert rules, system info.
      </Typography>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Email sx={{ fontSize: 18 }} />} iconPosition="start" label="Email" />
          <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label="Database" />
          <Tab icon={<Notifications sx={{ fontSize: 18 }} />} iconPosition="start" label="Alert Rules" />
          <Tab icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" label="System" />
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          {/* ===== EMAIL TAB ===== */}
          <TabPanel value={tab} index={0}>
            <SectionTitle icon={<Email color="primary" />} title="SMTP Configuration"
              subtitle="Configure email server for automated alert notifications" />
            
            {smtpStatus && (
              <Alert severity={smtpStatus.configured ? 'success' : 'warning'} sx={{ mb: 3 }}>
                {smtpStatus.configured
                  ? `SMTP configured — Sending from ${smtpStatus.smtp_from}`
                  : 'SMTP not configured — Alert emails will not be sent'}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField label="SMTP Host" value={smtpConfig.smtp_host} size="small"
                placeholder="smtp.gmail.com"
                onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_host: e.target.value })} />
              <TextField label="SMTP Port" type="number" value={smtpConfig.smtp_port} size="small"
                onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) || 587 })} />
              <TextField label="Username" value={smtpConfig.smtp_user} size="small"
                placeholder="alerts@yourdomain.com"
                onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_user: e.target.value })} />
              <TextField label="Password" type="password" value={smtpConfig.smtp_password} size="small"
                placeholder={smtpStatus?.smtp_password_set ? '••••••••' : 'Enter password'}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })} />
              <TextField label="From Address" value={smtpConfig.smtp_from} size="small"
                placeholder="Same as username if empty"
                onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from: e.target.value })} />
              <FormControlLabel
                control={<Switch checked={smtpConfig.smtp_use_tls}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_use_tls: e.target.checked })} />}
                label="Use TLS" />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button variant="contained" startIcon={<Save />} onClick={saveSmtp} disabled={smtpSaving}>
                {smtpSaving ? 'Saving...' : 'Save Configuration'}
              </Button>
              <Button variant="outlined" startIcon={<Send />} onClick={testEmail}
                disabled={smtpTesting || !smtpStatus?.configured}>
                {smtpTesting ? 'Sending...' : 'Send Test Email'}
              </Button>
            </Box>

            {smtpStatus?.super_admin_emails?.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>Recipients (all Super Admins):</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {smtpStatus.super_admin_emails.map((email: string) => (
                    <Chip key={email} label={email} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* ===== DATABASE TAB ===== */}
          <TabPanel value={tab} index={1}>
            <SectionTitle icon={<Storage color="primary" />} title="Telemetry Database"
              subtitle="MongoDB storage for robot telemetry data — battery, pose, diagnostics" />
            
            {dbLoading && <LinearProgress sx={{ mb: 2 }} />}

            {dbStats && (
              <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {dbStats.document_count?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Total Records</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {dbStats.size_mb || 0} MB
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Data Size</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {dbStats.storage_mb || 0} MB
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Storage Used</Typography>
                </Paper>
              </Box>
              {(!dbStats.document_count || dbStats.document_count === 0) && !dbStats.error && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No telemetry data yet. Data will appear once a robot connects and starts sending telemetry. Make sure your robot's <code>cloud_bridge_node</code> is running and connected.
                </Alert>
              )}
              {dbStats.error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  MongoDB query issue: {dbStats.error}. This is normal if no telemetry has been received yet.
                </Alert>
              )}
              </>
            )}

            {dbStats?.per_robot?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Records per Robot:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dbStats.per_robot.map((r: any) => (
                    <Chip key={r.robot_serial} label={`${r.robot_serial}: ${r.count.toLocaleString()}`}
                      size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <SectionTitle icon={<Schedule color="warning" />} title="Auto-Expiry (TTL Index)"
              subtitle="Automatically delete old telemetry data after N days" />

            {ttlStatus && (
              <Alert severity={ttlStatus.configured ? 'success' : 'info'} sx={{ mb: 2 }}>
                {ttlStatus.configured
                  ? `TTL active: data expires after ${ttlStatus.ttl.expire_after_days} days`
                  : 'No TTL configured — telemetry data is retained indefinitely'}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField label="TTL (days)" type="number" size="small" value={ttlDays}
                sx={{ width: 120 }}
                onChange={(e) => setTtlDays(Math.max(1, parseInt(e.target.value) || 30))}
                inputProps={{ min: 1, max: 365 }} />
              <Button variant="contained" size="small" onClick={setTtl}>Set TTL</Button>
              {ttlStatus?.configured && (
                <Button variant="outlined" size="small" color="warning" onClick={removeTtl}>
                  Remove TTL
                </Button>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              Recommended: 30 days for small fleets, 7–14 days for 50+ robots.
              MongoDB automatically deletes expired documents within ~60 seconds of TTL expiry.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <SectionTitle icon={<DeleteForever color="error" />} title="Manual Purge"
              subtitle="Immediately delete telemetry data older than a specified number of days" />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <TextField label="Older than (days)" type="number" size="small" value={purgeDays}
                sx={{ width: 150 }}
                onChange={(e) => setPurgeDays(Math.max(1, parseInt(e.target.value) || 30))} />
              <Button variant="contained" color="error" size="small" startIcon={<DeleteForever />}
                onClick={purgeData}>Purge Now</Button>
              <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadDbStats}>
                Refresh Stats
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <SectionTitle icon={<Info color="info" />} title="Telemetry Push Rate"
              subtitle="How often each robot sends telemetry to the cloud — saves directly to robot config file" />

            <Alert severity="info" sx={{ mb: 2 }}>
              Telemetry is <strong>only sent in ACTIVE mode</strong> (when a user is logged in and viewing the robot).
              In RESTRICTED (privacy) mode, no telemetry is pushed.
              For fleets of 50+ robots, 30–60 seconds is recommended to reduce DB load.
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Target Robot</InputLabel>
                <Select
                  value={selectedTelemetryRobot}
                  label="Target Robot"
                  onChange={(e) => setSelectedTelemetryRobot(e.target.value)}
                >
                  <MenuItem value="__all__">All Robots</MenuItem>
                  {telemetryRateRobots.map((r: any) => (
                    <MenuItem key={r.serial_number} value={r.serial_number}>
                      {r.serial_number}{r.friendly_name ? ` (${r.friendly_name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Interval (seconds)"
                type="number"
                size="small"
                value={telemetryRate}
                sx={{ width: 150 }}
                onChange={(e) => setTelemetryRate(Math.max(1, Math.min(300, parseInt(e.target.value) || 5)))}
                inputProps={{ min: 1, max: 300 }}
                helperText={telemetryRate <= 3 ? 'High bandwidth' : telemetryRate <= 10 ? 'Recommended' : telemetryRate <= 30 ? 'Low bandwidth' : 'Minimal updates'}
              />

              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={saveTelemetryRate}
                disabled={telemetryRateSaving}
              >
                {telemetryRateSaving ? 'Saving...' : 'Save to Robot'}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              This sends a <code>config_update</code> with <code>telemetry_interval</code> to the robot
              and saves it to <code>/etc/robot/robot_config.yaml</code> on the robot. Takes effect immediately — no restart needed.
            </Typography>

            {telemetryRateRobots.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Current Rate Per Robot:</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Robot</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Telemetry Interval</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {telemetryRateRobots.map((r: any) => {
                        const cfg = r.robot_config || {};
                        const interval = cfg.telemetry_interval ?? cfg.telemetry_rate_hz ?? 5;
                        return (
                          <TableRow key={r.serial_number} hover
                            onClick={() => { setSelectedTelemetryRobot(r.serial_number); setTelemetryRate(interval); }}
                            sx={{ cursor: 'pointer', bgcolor: selectedTelemetryRobot === r.serial_number ? 'action.selected' : undefined }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {r.serial_number}{r.friendly_name ? ` (${r.friendly_name})` : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{r.serial_number}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={r.status === 'online' ? 'Online' : 'Offline'} size="small"
                                color={r.status === 'online' ? 'success' : 'default'} variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} color={interval > 30 ? 'warning.main' : 'text.primary'}>
                                {interval}s
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Click a row to select that robot and set its interval. Data from cached robot config — reflects last known value.
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <SectionTitle icon={<CloudUpload color="success" />} title="Database Backup & Restore"
              subtitle="Create, download, and restore full PostgreSQL database backups (robots, users, settings)" />

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={backupCreating ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                onClick={createBackup}
                disabled={backupCreating}
              >
                {backupCreating ? 'Creating Backup...' : 'Create Backup Now'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={<GetApp sx={{ transform: 'rotate(180deg)' }} />}
              >
                Upload Backup
                <input type="file" hidden accept=".sql,.gz,.tar.gz"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await apiClient.post('/admin/system/backup/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      showMsg(res.data.message || 'Backup uploaded');
                      await loadBackups();
                    } catch (err: any) {
                      showMsg(err.response?.data?.detail || 'Upload failed', 'error');
                    }
                    e.target.value = '';
                  }}
                />
              </Button>
              <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadBackups}>
                Refresh
              </Button>
            </Box>

            {backupLoading && <LinearProgress sx={{ mb: 2 }} />}

            {backups.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No backups found. Create your first backup to protect your data.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Backup File</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell align="right" sx={{ width: 180 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((b: any) => (
                      <TableRow key={b.filename} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {b.filename}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(b.created_at * 1000).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{b.size_mb} MB</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => downloadBackup(b.filename)} color="primary">
                              <GetApp sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore from this backup">
                            <span>
                              <IconButton size="small" onClick={() => restoreBackup(b.filename)}
                                color="warning" disabled={restoring === b.filename}>
                                {restoring === b.filename
                                  ? <CircularProgress size={16} />
                                  : <SettingsBackupRestore sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete backup">
                            <IconButton size="small" onClick={() => deleteBackup(b.filename)} color="error">
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Backups include all PostgreSQL data: robots, users, settings, maps metadata, alert rules.
              Telemetry data (MongoDB) is not included — use TTL/purge above to manage its size.
              Download backups to your workstation for off-site storage.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <SectionTitle icon={<Schedule color="primary" />} title="Automatic Backup Schedule"
              subtitle="Schedule daily backups with automatic rotation of old backups" />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch checked={autoBackupConfig.enabled}
                    onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, enabled: e.target.checked }))} />
                }
                label="Enable daily auto-backup"
              />

              <TextField
                label="Hour (0-23)" type="number" size="small"
                value={autoBackupConfig.hour}
                onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 0)) }))}
                disabled={!autoBackupConfig.enabled}
                sx={{ width: 100 }}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                label="Minute" type="number" size="small"
                value={autoBackupConfig.minute}
                onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, minute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) }))}
                disabled={!autoBackupConfig.enabled}
                sx={{ width: 100 }}
                inputProps={{ min: 0, max: 59 }}
              />
              <TextField
                label="Keep last N backups" type="number" size="small"
                value={autoBackupConfig.retention}
                onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, retention: Math.max(1, Math.min(90, parseInt(e.target.value) || 7)) }))}
                disabled={!autoBackupConfig.enabled}
                sx={{ width: 150 }}
                inputProps={{ min: 1, max: 90 }}
              />
              <Button variant="contained" size="small" onClick={saveAutoBackupConfig}
                disabled={autoBackupSaving}
                startIcon={autoBackupSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}>
                {autoBackupSaving ? 'Saving...' : 'Save Schedule'}
              </Button>
            </Box>

            {autoBackupConfig.enabled && (
              <Alert severity="success" sx={{ mb: 1 }}>
                Auto-backup runs daily at <strong>{String(autoBackupConfig.hour).padStart(2, '0')}:{String(autoBackupConfig.minute).padStart(2, '0')}</strong> (server time).
                Keeps the last <strong>{autoBackupConfig.retention}</strong> backups, older ones are automatically deleted.
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Auto-backups appear in the list above and can be downloaded or restored like manual backups.
              Schedule uses server time (UTC). Backups are stored on the server in the backups/ directory.
            </Typography>
          </TabPanel>

          {/* ===== ALERT RULES TAB ===== */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <SectionTitle icon={<Notifications color="primary" />} title="Alert Rules"
                subtitle="Configure automated alerts for fleet anomalies" />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={seedDefaults}>
                  Seed Defaults
                </Button>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openRuleDialog()}>
                  New Rule
                </Button>
              </Box>
            </Box>

            {alertsLoading && <LinearProgress sx={{ mb: 2 }} />}

            {alertRules.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No alert rules configured. Click "Seed Defaults" to create standard rules (battery low, robot offline, etc.)
              </Alert>
            ) : (
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Active</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Robot</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Cooldown</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alertRules.map((rule: any) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <Switch checked={rule.is_active} size="small" onChange={() => toggleRule(rule)} />
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{rule.name}</Typography></TableCell>
                        <TableCell><Chip label={rule.trigger_type} size="small" variant="outlined" /></TableCell>
                        <TableCell><Typography variant="caption" fontFamily="monospace">{rule.event_type}</Typography></TableCell>
                        <TableCell>{rule.robot_serial || <Typography variant="caption" color="text.secondary">All</Typography>}</TableCell>
                        <TableCell>
                          <Chip label={rule.severity} size="small"
                            sx={{ backgroundColor: severityColor[rule.severity] + '20', color: severityColor[rule.severity], fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{rule.cooldown_minutes}m</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openRuleDialog(rule)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteRule(rule.id)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Recent Alerts ({alertHistoryTotal} total)
              </Typography>
              <Button size="small" startIcon={<Refresh />} onClick={loadAlerts}>Refresh</Button>
            </Box>

            {alertHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No alerts triggered yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Robot</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alertHistory.map((alert: any) => (
                      <TableRow key={alert.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">
                            {alert.triggered_at ? new Date(alert.triggered_at).toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{alert.rule_name}</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontFamily="monospace">{alert.robot_serial}</Typography></TableCell>
                        <TableCell>
                          <Chip label={alert.severity} size="small"
                            sx={{ backgroundColor: severityColor[alert.severity] + '20', color: severityColor[alert.severity], fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={alert.details || ''}>
                            <Typography variant="caption" sx={{ maxWidth: 250, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {alert.details}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {alert.email_sent
                            ? <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                            : <Tooltip title={alert.email_error || 'Not sent'}><ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} /></Tooltip>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ===== SYSTEM TAB ===== */}
          <TabPanel value={tab} index={3}>
            <SectionTitle icon={<Settings color="primary" />} title="System Information"
              subtitle="Server and database connection overview" />
            
            {systemInfo ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Application</Typography>
                  <Typography variant="body2">Name: {systemInfo.app_name}</Typography>
                  <Typography variant="body2">Version: {systemInfo.version}</Typography>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Server Resources</Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption">CPU: {systemInfo.system?.cpu_percent}%</Typography>
                    <LinearProgress variant="determinate" value={systemInfo.system?.cpu_percent || 0}
                      color={systemInfo.system?.cpu_percent > 80 ? 'error' : 'primary'} />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption">Memory: {systemInfo.system?.memory_percent}%</Typography>
                    <LinearProgress variant="determinate" value={systemInfo.system?.memory_percent || 0}
                      color={systemInfo.system?.memory_percent > 80 ? 'error' : 'primary'} />
                  </Box>
                  <Box>
                    <Typography variant="caption">Disk: {systemInfo.system?.disk_percent}%</Typography>
                    <LinearProgress variant="determinate" value={systemInfo.system?.disk_percent || 0}
                      color={systemInfo.system?.disk_percent > 80 ? 'error' : 'primary'} />
                  </Box>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>PostgreSQL</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                    {systemInfo.postgresql?.host}
                  </Typography>
                  <Typography variant="body2">DB: {systemInfo.postgresql?.database}</Typography>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>MongoDB</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                    {systemInfo.mongodb?.host}
                  </Typography>
                  <Typography variant="body2">DB: {systemInfo.mongodb?.database}</Typography>
                </Paper>
              </Box>
            ) : (
              <CircularProgress />
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* ===== Rule Create/Edit Dialog ===== */}
      <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Rule Name" value={ruleForm.name} fullWidth size="small"
              placeholder="e.g., Low Battery Warning"
              onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
            <TextField label="Trigger Type" select value={ruleForm.trigger_type} fullWidth size="small"
              onChange={(e) => setRuleForm({ ...ruleForm, trigger_type: e.target.value })}>
              <MenuItem value="event">Event — triggers on single event</MenuItem>
              <MenuItem value="threshold">Threshold — value crosses limit</MenuItem>
              <MenuItem value="absence">Absence — no event for N minutes</MenuItem>
              <MenuItem value="pattern">Pattern — N events in M minutes</MenuItem>
            </TextField>
            <TextField label="Event Type" select value={ruleForm.event_type} fullWidth size="small"
              onChange={(e) => setRuleForm({ ...ruleForm, event_type: e.target.value })}>
              <MenuItem value="telemetry">Telemetry</MenuItem>
              <MenuItem value="robot_offline">Robot Offline</MenuItem>
              <MenuItem value="robot_online">Robot Online</MenuItem>
              <MenuItem value="robot_ip_changed">Robot IP Changed</MenuItem>
              <MenuItem value="battery_low">Battery Low</MenuItem>
              <MenuItem value="license_alert">License Alert</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </TextField>
            <TextField label="Robot Serial (empty = all robots)" value={ruleForm.robot_serial} fullWidth size="small"
              onChange={(e) => setRuleForm({ ...ruleForm, robot_serial: e.target.value })} />
            <TextField label="Severity" select value={ruleForm.severity} fullWidth size="small"
              onChange={(e) => setRuleForm({ ...ruleForm, severity: e.target.value })}>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="emergency">Emergency</MenuItem>
            </TextField>
            <TextField label="Cooldown (minutes)" type="number" value={ruleForm.cooldown_minutes} fullWidth size="small"
              onChange={(e) => setRuleForm({ ...ruleForm, cooldown_minutes: parseFloat(e.target.value) || 15 })} />

            {ruleForm.trigger_type === 'threshold' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 1 }}>
                <TextField label="Field" size="small" placeholder="battery.percentage"
                  value={ruleForm.condition.field || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition: { ...ruleForm.condition, field: e.target.value } })} />
                <TextField label="Operator" select size="small" value={ruleForm.condition.operator || '<'}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition: { ...ruleForm.condition, operator: e.target.value } })}>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="==">==</MenuItem>
                </TextField>
                <TextField label="Value" type="number" size="small" value={ruleForm.condition.value || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition: { ...ruleForm.condition, value: parseFloat(e.target.value) } })} />
              </Box>
            )}

            {ruleForm.trigger_type === 'absence' && (
              <TextField label="Timeout (minutes)" type="number" size="small"
                value={ruleForm.condition.timeout_minutes || 5}
                onChange={(e) => setRuleForm({ ...ruleForm, condition: { timeout_minutes: parseInt(e.target.value) || 5 } })} />
            )}

            {ruleForm.trigger_type === 'pattern' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField label="Count" type="number" size="small" value={ruleForm.condition.count || 3}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition: { ...ruleForm.condition, count: parseInt(e.target.value) || 3 } })} />
                <TextField label="Window (minutes)" type="number" size="small" value={ruleForm.condition.window_minutes || 10}
                  onChange={(e) => setRuleForm({ ...ruleForm, condition: { ...ruleForm.condition, window_minutes: parseInt(e.target.value) || 10 } })} />
              </Box>
            )}

            <TextField label="Suggested Action" value={ruleForm.suggested_action} fullWidth size="small" multiline rows={2}
              placeholder="What should the admin do when this alert fires?"
              onChange={(e) => setRuleForm({ ...ruleForm, suggested_action: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRule} disabled={!ruleForm.name || !ruleForm.event_type}>
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemSettings;
